'use client';

import NumberField from '@koda/koda-components/molecules/NumberField';
import { getLatestQuarterLabel, getLatestQuarterYear } from '@koda/koda-utils/date';
import { useLocale, useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { DANCE_SCHOOL_CONTENT_PATH, createDanceSchoolDefaults, danceSchoolSchema } from './danceSchool.Schema';
import FormHeadline from './shared/FormHeadline';

export default function DanceSchoolComponent(): ReactElement {
  const licenseCode = LicenseTypes.DanceSchool;
  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: danceSchoolSchema,
    defaults: createDanceSchoolDefaults,
  });
  const t = useTranslations(DANCE_SCHOOL_CONTENT_PATH);
  const locale = useLocale();

  return (
    <>
      <NumberField
        label={t('lessonsQuarterly.label', {
          quarter: getLatestQuarterLabel(locale),
          year: getLatestQuarterYear(),
        })}
        name="lessonsQuarterly"
        placeholder={t('lessonsQuarterly.placeholder')}
        required
        disabled={costCalculated}
        value={values.lessonsQuarterly}
        onChange={e => setField('lessonsQuarterly', parseInt(e.target.value))}
        error={showErrors ? getError('lessonsQuarterly') : undefined}
        validated={!!values.lessonsQuarterly && !getError('lessonsQuarterly')}
        data-error-field={showErrors && getError('lessonsQuarterly') ? 'true' : undefined}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <NumberField
        label={t('averageStudentsPerLesson.label', {
          quarter: getLatestQuarterLabel(locale),
          year: getLatestQuarterYear(),
        })}
        name="averageStudentsPerLesson"
        placeholder={t('averageStudentsPerLesson.placeholder')}
        required
        disabled={costCalculated}
        value={values.averageStudentsPerLesson}
        onChange={e => setField('averageStudentsPerLesson', parseInt(e.target.value))}
        error={showErrors ? getError('averageStudentsPerLesson') : undefined}
        validated={!!values.averageStudentsPerLesson && !getError('averageStudentsPerLesson')}
        data-error-field={showErrors && getError('averageStudentsPerLesson') ? 'true' : undefined}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <FormHeadline title={t('eventsSection.title')} description={t('eventsSection.description')} />
      <NumberField
        label={t('eventsQuarterly.label', {
          quarter: getLatestQuarterLabel(locale),
          year: getLatestQuarterYear(),
        })}
        name="eventsQuarterly"
        placeholder={t('eventsQuarterly.placeholder')}
        disabled={costCalculated}
        value={values.eventsQuarterly}
        optionalText={t('eventsQuarterly.optionalText')}
        onChange={e => setField('eventsQuarterly', parseInt(e.target.value))}
        error={showErrors ? getError('eventsQuarterly') : undefined}
        validated={values.eventsQuarterly !== undefined && !getError('eventsQuarterly')}
        data-error-field={showErrors && getError('eventsQuarterly') ? 'true' : undefined}
        min={0}
        sign="positive-only"
        styleVariant="border"
      />
    </>
  );
}
